import { css } from 'aphrodite';
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { formatDealSizeAxisTick } from '../../helpers/formatDealSizeAxisTick';
import type { TagAggregateRow } from '../../helpers/opportunityReportingChartTypes';
import { styleSheet } from './styles';

function OpportunitiesByTagTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: TagAggregateRow & { color: string } }>;
}) {
	if (!active || !payload?.length) {
		return null;
	}
	const row = payload[0]?.payload;
	if (!row) {
		return null;
	}
	return (
		<div className={css(styleSheet.tooltip)}>
			<p className={css(styleSheet.tooltipLabel)}>{row.tag}</p>
			<p className={css(styleSheet.tooltipValue)}>Deal size: {formatCurrency(row.dealSize)}</p>
			<p className={css(styleSheet.tooltipValue)}>Tag rows: {row.count}</p>
		</div>
	);
}

interface OpportunitiesByTagChartProps {
	data: TagAggregateRow[];
	isLoading?: boolean;
	className?: string;
}

export const OpportunitiesByTagChart: React.FC<OpportunitiesByTagChartProps> = ({ data, isLoading, className }) => {
	const chartData = useMemo(() => data.map(item => ({ ...item, color: mainChartColor() })), [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No tag data in the loaded opportunities</div>
			</div>
		);
	}

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h2 className={css(styleSheet.title)}>Deal size by tag</h2>
			<ResponsiveContainer width='100%' height={360}>
				<BarChart data={chartData} margin={{ top: 8, right: 20, left: 8, bottom: 80 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='tag'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 11, fill: THEME.colors.text.main }}
						angle={-35}
						textAnchor='end'
						height={90}
					/>
					<YAxis
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 11, fill: THEME.colors.text.main }}
						tickFormatter={formatDealSizeAxisTick}
					/>
					<Tooltip content={<OpportunitiesByTagTooltip />} />
					<Bar dataKey='dealSize' radius={[4, 4, 0, 0]}>
						{chartData.map((item, i) => (
							<Cell key={`${item.tag}-${i}`} fill={item.color} />
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
